import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import type { FreightBrowserFormState } from '@shipstation/client-common/components/freight-browser';
import { getChangedFieldPaths } from '@shipstation/client-common/utils/get-changed-field-paths';
import { useEnableLtlWizardFormChangeTracking } from '../../../../feature-flags/hooks/use-enable-ltl-wizard-form-change-tracking';
import { Segment } from '../../../../services/analytics-services/segment';
import { LtlFreightTrackingEvents } from '../../tracking-events';

type UseTrackFreightBrowserChangeParams = {
  formStateRef: MutableRefObject<FreightBrowserFormState>;
  workflowGuidRef: MutableRefObject<string>;
};

export const useTrackFreightBrowserChange = ({
  formStateRef,
  workflowGuidRef,
}: UseTrackFreightBrowserChangeParams): ((next: FreightBrowserFormState) => void) => {
  const isEnabled = useEnableLtlWizardFormChangeTracking();

  return useCallback(
    (next: FreightBrowserFormState) => {
      if (!isEnabled) return;

      const fieldsChanged = getChangedFieldPaths(formStateRef.current, next);

      // Deferred to the end of the event queue so tracking calls never compete with
      // the form's own in-flight requests (auto-browse, etc.) for the main thread.
      fieldsChanged.forEach((fieldChanged) => {
        setTimeout(() => {
          Segment.trackEvent(LtlFreightTrackingEvents.BROWSER_FORM_CHANGED, {
            workflowGuid: workflowGuidRef.current,
            fieldChanged,
          });
        }, 0);
      });
    },
    [isEnabled, formStateRef, workflowGuidRef],
  );
};
